import { z } from 'zod';
import { gstinSchema } from '../money/gst.js';
import { shopDaySchema } from '../reports/date-ranges.js';
import {
  indianMobileSchema,
  objectIdSchema,
  isoDateStringSchema,
  paginationQuerySchema,
  stateCodeSchema,
} from './common.js';
import { customFieldValueInputSchema, customFieldValueSchema } from './custom-fields.js';
import { capturedMeasurementsSchema } from './measurement-profiles.js';

/**
 * The customer contract, shared by the browser and the API.
 *
 * This is the module the plan's "Zod schemas shared between client and server" rule exists
 * for. The counter flow types a mobile number into a form and the API decides whether that
 * number already belongs to somebody — if the two sides disagreed by so much as a stripped
 * `+91`, the shop would create a second record for a customer it already has. So the
 * NORMALISING schema (`indianMobileSchema`) is imported by both, and neither side owns a
 * private copy of it.
 */

/**
 * The `entityType` a customer's photographs are filed under in the media pipeline.
 *
 * Declared here rather than only in the API's model because the form uploads them: the browser
 * has to name the same bucket the server files them into, and two spellings of the word would
 * put a customer's photo somewhere their own page never looks.
 */
export const CUSTOMER_MEDIA_ENTITY_TYPE = 'customer';

/** How a shop found out about a customer. Closed, so the report by source is groupable. */
export const CUSTOMER_SOURCES = [
  'walk_in',
  'referral',
  'repeat',
  'instagram',
  'whatsapp',
  'facebook',
  'google',
  'exhibition',
  'other',
] as const;

export type CustomerSource = (typeof CUSTOMER_SOURCES)[number];
export const customerSourceSchema = z.enum(CUSTOMER_SOURCES);

/**
 * Who the shop is billing — a person, or a business.
 *
 * The distinction earns a field of its own rather than a tag because ONE thing downstream
 * depends on it: a registered shop billing a business puts that business's GSTIN on the
 * invoice, and a customer who cannot hold a GSTIN is the whole reason the counter never has to
 * think about it for the other ninety per cent of walk-ins. `b2c` is the default for exactly
 * that reason — a shop that never sells to businesses should never see the second field.
 */
export const CUSTOMER_KINDS = ['b2c', 'b2b'] as const;
export type CustomerKind = (typeof CUSTOMER_KINDS)[number];
export const customerKindSchema = z.enum(CUSTOMER_KINDS);

/** What a customer created before this shipped, and every walk-in since, is. */
export const DEFAULT_CUSTOMER_KIND: CustomerKind = 'b2c';

const GSTIN_ON_CONSUMER = {
  message: 'Only a business customer can hold a GSTIN',
  path: ['gstin'] as const,
};

/**
 * The one cross-field rule on a customer: a GSTIN belongs to a business.
 *
 * Stated as a function because it is checked in three places that must agree — on create, on
 * an update that carries both halves, and in the service against the RESOLVED pair after a
 * patch that carries only one. A consumer holding a GSTIN is not a harmless stray value: the
 * invoice reads it, so it would print somebody's tax number on a bill they never gave it for.
 */
export function customerGstinFitsKind(pair: {
  kind?: CustomerKind | null;
  gstin?: string | null;
}): boolean {
  const gstin = pair.gstin?.trim();
  if (!gstin) return true;
  return (pair.kind ?? DEFAULT_CUSTOMER_KIND) === 'b2b';
}

/* -------------------------------------------------------------------------------------------
 * The two dates a shop greets somebody on
 * ---------------------------------------------------------------------------------------- */

/**
 * A birthday and an anniversary are CALENDAR DAYS, not instants, and are stored as
 * `YYYY-MM-DD` strings for the same reason attendance stores its day that way: a date of birth
 * held as a timestamp at UTC midnight renders as the day before in half the world, and the one
 * thing a greeting must get right is which day it is.
 *
 * The YEAR is kept because shops ask for it and it is occasionally useful ("she turns 60 this
 * year"), but nothing in the product matches on it — see `occasionDayKey`.
 */
export const customerOccasionDateSchema = shopDaySchema;

/**
 * `MM-DD` — the half of an occasion date that a greeting actually fires on.
 *
 * Derived on write and indexed, exactly as `nameSearchKeys` and `phoneSearchKeys` are, so
 * "whose birthday falls this week" is an index lookup rather than a scan with date arithmetic
 * over every customer in the shop.
 *
 * 29 February is stored as it was given and simply does not match in a common year. Greeting a
 * leap-year birthday on 28 February or 1 March is a guess about somebody's preference, and a
 * guess made silently on their behalf is worse than the shop noticing and sending nothing.
 */
export function occasionDayKey(day: string): string {
  return day.slice(5);
}

/* -------------------------------------------------------------------------------------------
 * Who the person is
 * ---------------------------------------------------------------------------------------- */

/**
 * Recorded because a tailor's whole trade turns on it: the garment types offered, the
 * measurement profile taken and the way a customer is addressed in a reminder all differ, and
 * a shop that had to infer it from a first name would guess wrong on every third walk-in.
 *
 * `other` rather than a free string, so the field stays groupable in a report. A shop that
 * needs a finer answer than these three has custom fields for it.
 */
export const CUSTOMER_GENDERS = ['male', 'female', 'other'] as const;
export type CustomerGender = (typeof CUSTOMER_GENDERS)[number];
export const customerGenderSchema = z.enum(CUSTOMER_GENDERS);

export const CUSTOMER_TAG_MAX_LENGTH = 30;
export const MAX_TAGS_PER_CUSTOMER = 20;
export const CUSTOMER_NAME_MAX_LENGTH = 120;
export const CUSTOMER_NOTES_MAX_LENGTH = 2000;
export const CUSTOMER_PROFESSION_MAX_LENGTH = 100;
export const CUSTOMER_PREFERRED_STYLE_MAX_LENGTH = 200;

/**
 * A tag the shop invents for itself — "bridal", "wholesale", "regular".
 *
 * Stored in the shop's own casing (an owner who types "Bridal" should see "Bridal"), but
 * compared case-insensitively everywhere, which is why the API folds case before it dedupes
 * and before it filters.
 */
export const customerTagSchema = z
  .string()
  .trim()
  .min(1, 'A tag cannot be empty')
  .max(CUSTOMER_TAG_MAX_LENGTH, `A tag can be at most ${CUSTOMER_TAG_MAX_LENGTH} characters`)
  .transform((value) => value.replace(/\s+/g, ' '));

export const customerNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter the customer’s name')
  .max(CUSTOMER_NAME_MAX_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

/* -------------------------------------------------------------------------------------------
 * One name, stored twice
 *
 * The form asks for a first name and a last name, because that is how a counter takes a name
 * down and how a shop wants it back on a reminder. Everything downstream — the order, the
 * invoice, the search index, the audit label — reads a single `name`, and rewriting all of them
 * to join two fields would be a large change with nothing to show for it.
 *
 * So `name` stays the ONE canonical field and the halves are kept beside it, with a single pair
 * of functions deciding how the two representations correspond. Both directions are needed and
 * both are used: a customer typed into the form arrives as halves and is JOINED; a customer
 * arriving from a CSV import, a lead conversion or the counter's quick add has only a full name
 * and is SPLIT. The migration that backfilled existing customers used the same `splitName`, so a
 * record created before the split and one created after are indistinguishable afterwards.
 * ---------------------------------------------------------------------------------------- */

/** The half of a name that goes in the second box. Everything before the last space is the first. */
export function splitCustomerName(name: string): { firstName: string; lastName: string | null } {
  const collapsed = name.trim().replace(/\s+/g, ' ');
  const cut = collapsed.lastIndexOf(' ');
  // A single word is entirely a first name. "Priya" has no surname to invent, and putting the
  // only word somebody gave into the LAST name box would show an empty first name on a form
  // whose first name is the required field.
  if (cut < 0) return { firstName: collapsed, lastName: null };
  return { firstName: collapsed.slice(0, cut), lastName: collapsed.slice(cut + 1) };
}

/** The inverse. A missing or blank last name simply leaves the first name standing alone. */
export function joinCustomerName(firstName: string, lastName?: string | null): string {
  const last = lastName?.trim() ?? '';
  const first = firstName.trim();
  return (last ? `${first} ${last}` : first).replace(/\s+/g, ' ');
}

export const customerAddressSchema = z.object({
  line1: z.string().trim().max(200).nullish(),
  line2: z.string().trim().max(200).nullish(),
  city: z.string().trim().max(100).nullish(),
  stateCode: stateCodeSchema.nullish(),
  pincode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'A PIN code is 6 digits')
    .nullish(),
});
export type CustomerAddress = z.infer<typeof customerAddressSchema>;

const customerFieldsSchema = z.object({
  /**
   * The full name, and still the canonical one — see `splitCustomerName`.
   *
   * OPTIONAL rather than required, which is the one subtlety in this object: a caller may send
   * the halves instead, and `customerNameGiven` below refuses the case where neither arrives.
   * Every caller that predates the split — the CSV import, lead conversion, the counter's quick
   * add — keeps sending only this, and the service derives the halves from it.
   */
  name: customerNameSchema.optional(),
  /** The first box on the form. Sent with `lastName`; `name` is derived from the pair. */
  firstName: customerNameSchema.optional(),
  lastName: customerNameSchema.nullish(),
  primaryPhone: indianMobileSchema,
  /**
   * A second number to try, and the number to message on. Both are separate fields rather than
   * one "other numbers" list because they are asked for different things: the alternative is
   * the one a shop rings when the first does not answer, and the WhatsApp number is the one a
   * reminder is delivered to — often a family handset that nobody answers calls on.
   *
   * Both are indexed for search alongside the primary, so "the one ending 4210" finds a
   * customer by whichever of their numbers the counter happens to remember.
   */
  altPhone: indianMobileSchema.nullish(),
  whatsappPhone: indianMobileSchema.nullish(),
  /** Man, woman, or neither — what the shop cuts for. See `CUSTOMER_GENDERS`. */
  gender: customerGenderSchema.nullish(),
  /** "Advocate", "school teacher" — a boutique's shorthand for what a customer dresses for. */
  profession: z.string().trim().max(CUSTOMER_PROFESSION_MAX_LENGTH).nullish(),
  /**
   * What this customer likes, in the shop's own words — "high neck, three-quarter sleeve".
   *
   * Kept apart from `notes` on purpose. Notes are whatever anybody thought worth writing down;
   * this is the one line a tailor re-reads before cutting, and burying it in a free-text box
   * with a delivery instruction and a phone-call summary is how it gets missed.
   */
  preferredStyle: z.string().trim().max(CUSTOMER_PREFERRED_STYLE_MAX_LENGTH).nullish(),
  /** Person or business. Absent means person, so every existing caller keeps working. */
  kind: customerKindSchema.optional(),
  /** The business's tax number. Only a `b2b` customer may carry one — see `customerGstinFitsKind`. */
  gstin: gstinSchema.nullish(),
  /** `YYYY-MM-DD`. The greeting fires on the month and day; the year is kept but not matched. */
  dateOfBirth: customerOccasionDateSchema.nullish(),
  /** The other date a shop remembers somebody by — a wedding, most often. */
  anniversary: customerOccasionDateSchema.nullish(),
  email: z.string().trim().email('Enter a valid email address').max(200).nullish(),
  address: customerAddressSchema.nullish(),
  notes: z.string().trim().max(CUSTOMER_NOTES_MAX_LENGTH).nullish(),
  tags: z.array(customerTagSchema).max(MAX_TAGS_PER_CUSTOMER).optional(),
  source: customerSourceSchema.nullish(),
  /** Id of a confirmed upload from the media pipeline (task 11). */
  photoMediaId: objectIdSchema.nullish(),
  /** Values for the fields this shop added to its customer book. See `custom-fields.ts`. */
  custom: z.array(customFieldValueInputSchema).optional(),
  /**
   * The staff member looking after this customer — the "Assigned to" an enquiry used to carry.
   * Must be somebody with an active membership in this shop; `null` unassigns. Checked by the
   * API, which is the only side that can see the roster.
   */
  assignedToUserId: objectIdSchema.nullish(),
});

/**
 * A customer must arrive with a name in one of its two spellings.
 *
 * `name` and `firstName` are each optional on their own so both kinds of caller are accepted —
 * the form sends halves, everything older sends the whole — but a body carrying NEITHER is a
 * customer nobody can find again, which is the one thing this book cannot store. The error is
 * reported against `name` rather than `firstName` so a caller that has never heard of the
 * halves gets a message about the field it actually sent.
 */
function customerNameGiven(fields: { name?: string; firstName?: string }): boolean {
  return Boolean(fields.name?.trim() || fields.firstName?.trim());
}

const NAME_MISSING = {
  message: 'Enter the customer’s name',
  path: ['name'] as const,
};

/**
 * How many sets of measurements may ride along with a new customer.
 *
 * Generous rather than tight: a woman arrives with her daughter and her mother and is measured
 * for two garments each, which is six before anybody has done anything unusual. The cap exists
 * only so one request cannot ask the server to open an unbounded transaction.
 */
export const MAX_CUSTOMER_INTAKE_MEASUREMENTS = 20;

export const createCustomerSchema = customerFieldsSchema
  .extend({
    /**
     * The override for the genuine case of two people sharing a number — a mother booking for
     * her daughter on one handset. Absent, a duplicate is refused with a 409 naming the
     * customer who already holds the number, so the counter can open THEM instead.
     */
    allowDuplicatePhone: z.boolean().optional(),
    /**
     * Measurements taken at the counter DURING this customer's creation.
     *
     * A profile is owned by a customer, so until now the shop had to save the person first and
     * measure her second — two screens for one visit, and a tape already in hand. These ride
     * with her instead, and the API writes them in the same transaction that creates her: the
     * same promise lead conversion already makes, that there is never a moment where the shop
     * has a customer and has mislaid her measurements.
     *
     * Absent on every existing caller — the CSV import, lead conversion, the counter's quick
     * add — and absent is not the same as empty, so none of them changes behaviour.
     */
    measurements: z
      .array(capturedMeasurementsSchema)
      .max(MAX_CUSTOMER_INTAKE_MEASUREMENTS)
      .optional(),
  })
  .refine(customerNameGiven, {
    message: NAME_MISSING.message,
    path: [...NAME_MISSING.path],
  })
  .refine(customerGstinFitsKind, {
    message: GSTIN_ON_CONSUMER.message,
    path: [...GSTIN_ON_CONSUMER.path],
  });
export type CreateCustomerInput = z.input<typeof createCustomerSchema>;

/**
 * Every field is optional, but sending `{}` is a mistake worth naming rather than a no-op.
 *
 * The kind/GSTIN rule is checked here only when a patch carries BOTH halves. A patch that sets
 * a GSTIN against a kind already stored — or drops a customer back to `b2c` while a GSTIN sits
 * on the document — is caught by the service, which re-validates the resolved pair rather than
 * the fragment it was sent.
 */
export const updateCustomerSchema = customerFieldsSchema
  .extend({ allowDuplicatePhone: z.boolean().optional() })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Nothing to update',
  })
  .refine((patch) => patch.kind === undefined || customerGstinFitsKind(patch), {
    message: GSTIN_ON_CONSUMER.message,
    path: [...GSTIN_ON_CONSUMER.path],
  });
export type UpdateCustomerInput = z.input<typeof updateCustomerSchema>;

/**
 * The buckets, deliberately NOT mutually exclusive — a new customer is usually also active, and
 * a high-value customer can be dormant. Only active and dormant are guaranteed disjoint.
 */
export const CUSTOMER_SEGMENTS = ['new', 'active', 'dormant', 'high_value'] as const;
export type CustomerSegment = (typeof CUSTOMER_SEGMENTS)[number];

export const CUSTOMER_SORT_FIELDS = [
  'recent',
  'name',
  'lifetimeValue',
  'lastOrder',
  'orderCount',
] as const;
export type CustomerSortField = (typeof CUSTOMER_SORT_FIELDS)[number];

/** The `assignedTo` filter value that means "nobody is looking after them". */
export const CUSTOMER_UNASSIGNED = 'unassigned';

/** The `orders` filter: never ordered (an enquiry), or ordered at least once. */
export const CUSTOMER_ORDER_FILTERS = ['none', 'some'] as const;
export type CustomerOrderFilter = (typeof CUSTOMER_ORDER_FILTERS)[number];

export const customerSearchQuerySchema = paginationQuerySchema.extend({
  /** Free text: a partial phone number, or a name spelled however the counter heard it. */
  q: z.string().trim().max(120).optional(),
  /** Tag filter, matched case-insensitively. Repeat the parameter for several tags. */
  tags: z
    .union([customerTagSchema, z.array(customerTagSchema)])
    .transform((value) => (Array.isArray(value) ? value : [value]))
    .optional(),
  /** Narrow the book to businesses, or to people. Absent means both. */
  kind: customerKindSchema.optional(),
  /**
   * Narrow the book to one of the tiles' buckets, resolved against the shop's own thresholds —
   * so the list behind "Dormant 12" is those twelve. Absent means every customer.
   */
  segment: z.enum(CUSTOMER_SEGMENTS).optional(),
  /** One staff member's customers, or `unassigned` for the ones nobody is looking after. */
  assignedTo: z.union([z.literal(CUSTOMER_UNASSIGNED), objectIdSchema]).optional(),
  /**
   * `none`: customers who have never ordered — the enquiries. `some`: everyone who has. Read off
   * the denormalised `orderCount`, so it costs no join.
   */
  orders: z.enum(CUSTOMER_ORDER_FILTERS).optional(),
  sortBy: z.enum(CUSTOMER_SORT_FIELDS).default('recent'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
});
export type CustomerSearchQuery = z.infer<typeof customerSearchQuerySchema>;

export const mergeCustomersSchema = z.object({
  /** The record that survives and receives everything. */
  survivorId: objectIdSchema,
  /** The record that is emptied and soft-deleted. */
  duplicateId: objectIdSchema,
});
export type MergeCustomersInput = z.infer<typeof mergeCustomersSchema>;

/** One customer as every screen renders it. */
export const customerSchema = z.object({
  id: objectIdSchema,
  /**
   * The shop's own running number for this customer — "S. No. 148" — assigned on create from
   * the tenant's gapless sequence and never reused.
   *
   * Read-only, and deliberately not part of any input schema: a number a client could choose is
   * a number two counters can choose at once. Null for a customer created before the sequence
   * existed and not reached by the backfill, which every screen renders as a dash.
   */
  serialNumber: z.number().int().positive().nullable().default(null),
  name: z.string(),
  /**
   * The two halves of `name`. Defaulted rather than required for the same reason `kind` is: a
   * response or a stored fixture recorded before the split still parses, and the parsed type
   * stays non-optional for every screen that reads it. The API always sends both.
   */
  firstName: z.string().default(''),
  lastName: z.string().nullable().default(null),
  primaryPhone: z.string(),
  altPhone: z.string().nullable().default(null),
  whatsappPhone: z.string().nullable().default(null),
  gender: customerGenderSchema.nullable().default(null),
  profession: z.string().nullable().default(null),
  preferredStyle: z.string().nullable().default(null),
  /**
   * The API always sends both. They carry defaults rather than being required so a response —
   * or a stored fixture — recorded before this shipped still parses, while the parsed type
   * stays non-optional for every screen that reads it.
   */
  kind: customerKindSchema.default(DEFAULT_CUSTOMER_KIND),
  gstin: z.string().nullable().default(null),
  dateOfBirth: z.string().nullable().default(null),
  anniversary: z.string().nullable().default(null),
  email: z.string().nullable(),
  address: z.object({
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    city: z.string().nullable(),
    stateCode: z.string().nullable(),
    pincode: z.string().nullable(),
  }),
  notes: z.string().nullable(),
  tags: z.array(z.string()),
  source: customerSourceSchema.nullable(),
  photoMediaId: z.string().nullable(),
  /**
   * The shop's own fields, stored as `custom-fields.ts` describes. The API always sends it;
   * optional here so a response (or a fixture) recorded before this shipped still parses.
   */
  custom: z.array(customFieldValueSchema).optional(),
  /** Denormalised statistics, maintained by the API — never written by a client. */
  orderCount: z.number().int().nonnegative(),
  lifetimeValuePaise: z.number().int().nonnegative(),
  lastOrderAt: isoDateStringSchema.nullable(),
  /**
   * Who is looking after this customer, and their display name. The API always sends both
   * (null when nobody is); optional so a response or fixture recorded before this shipped still
   * parses.
   */
  assignedToUserId: z.string().nullable().optional(),
  assignedToName: z.string().nullable().optional(),
  /**
   * The earliest due date among the customer's OPEN follow-up tasks — a cache the API keeps, never
   * written by a client. Null when nothing is open. Optional for the same reason as above.
   */
  nextFollowUpAt: isoDateStringSchema.nullable().optional(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Customer = z.infer<typeof customerSchema>;

export const customerPageSchema = z.object({
  items: z.array(customerSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type CustomerPage = z.infer<typeof customerPageSchema>;

/**
 * The colours a customer tag can wear. Keys rather than hex values, so the palette is drawn by
 * the web app in whatever shade reads in light and in dark mode, and a stored tag never carries
 * a colour that is illegible against one of them.
 */
export const CUSTOMER_TAG_COLOURS = [
  'red',
  'orange',
  'amber',
  'yellow',
  'green',
  'teal',
  'blue',
  'violet',
  'pink',
  'grey',
] as const;
export type CustomerTagColour = (typeof CUSTOMER_TAG_COLOURS)[number];
export const customerTagColourSchema = z.enum(CUSTOMER_TAG_COLOURS);

/**
 * How many tags a shop may create. A shop whose customers already carried more before tags were
 * managed keeps every one of them — it only cannot add another until it is back under.
 */
export const MAX_CUSTOMER_TAGS = 10;

/** One tag the shop manages, as the Manage Tags panel and the filter read it. */
export const customerTagDefinitionSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  colour: customerTagColourSchema,
  /** Live customers carrying it. */
  customerCount: z.number().int().nonnegative(),
  updatedAt: isoDateStringSchema,
  /** Who last created, renamed or recoloured it; null for one backfilled from existing data. */
  updatedBy: z.string().nullable(),
});
export type CustomerTagDefinition = z.infer<typeof customerTagDefinitionSchema>;

export const customerTagsSchema = z.object({
  tags: z.array(customerTagDefinitionSchema),
  limit: z.number().int().positive(),
});
export type CustomerTagList = z.infer<typeof customerTagsSchema>;

export const createCustomerTagSchema = z.object({
  name: customerTagSchema,
  /** Omitted, the next colour no tag is wearing yet. */
  colour: customerTagColourSchema.optional(),
});
export type CreateCustomerTagInput = z.infer<typeof createCustomerTagSchema>;

/**
 * A rename rewrites every customer who carries the tag; a colour change touches only the tag.
 * Renaming onto another tag the shop already has is refused rather than merged.
 */
export const updateCustomerTagSchema = z
  .object({
    name: customerTagSchema.optional(),
    colour: customerTagColourSchema.optional(),
  })
  .refine((patch) => patch.name !== undefined || patch.colour !== undefined, {
    message: 'Nothing to change',
  });
export type UpdateCustomerTagInput = z.infer<typeof updateCustomerTagSchema>;

/**
 * The body of the 409 a duplicate phone number produces. The client renders it as a choice —
 * "open Priya Sharma" or "add anyway" — so this shape is part of the contract, not an
 * incidental error detail.
 */
export const duplicatePhoneDetailsSchema = z.object({
  reason: z.literal('duplicate_phone'),
  existingCustomerId: objectIdSchema,
  existingCustomerName: z.string(),
});
export type DuplicatePhoneDetails = z.infer<typeof duplicatePhoneDetailsSchema>;

export const mergeResultSchema = z.object({
  survivor: customerSchema,
  /** What each module moved, keyed by module name — `{ media: 3 }`. Useful in the audit log. */
  moved: z.record(z.number().int().nonnegative()),
});
export type MergeResult = z.infer<typeof mergeResultSchema>;

/* -------------------------------------------------------------------------------------------
 * Segmentation
 *
 * "How many of these people still come back?" — answered from the denormalised statistics the
 * orders flow already maintains, and NEVER stored on the customer. A segment written into the
 * document would be wrong the minute an order landed, and every figure here is derivable from
 * `createdAt`, `lastOrderAt` and `lifetimeValuePaise` at the moment somebody asks.
 *
 * The thresholds live on the shop's settings rather than in this file as constants. A bridal
 * boutique where a customer returns twice a year and a uniform shop with weekly repeat trade
 * disagree about what "dormant" means, and both are right.
 * ---------------------------------------------------------------------------------------- */

/** Ten years. Long enough for any shop's idea of "still a customer", short enough to be a typo guard. */
export const MAX_SEGMENT_WINDOW_DAYS = 3650;

const customerSegmentationFields = z.object({
  /** Registered within this many days — the shop's own idea of a new name on the book. */
  newWindowDays: z.number().int().min(1).max(MAX_SEGMENT_WINDOW_DAYS),
  /** Ordered within this many days. */
  activeWindowDays: z.number().int().min(1).max(MAX_SEGMENT_WINDOW_DAYS),
  /** No activity at all for this many days. Must not be shorter than the active window. */
  dormantAfterDays: z.number().int().min(1).max(MAX_SEGMENT_WINDOW_DAYS),
  /** Lifetime value at or above this counts as high value. */
  highValuePaise: z.number().int().min(0),
});

/**
 * The one cross-field rule, and the reason it exists: active and dormant are the single pair of
 * buckets that must never overlap. An active window longer than the dormant threshold would let
 * the same customer be counted in both, and the tiles would silently stop adding up.
 */
const activeFitsInsideDormant = (settings: z.infer<typeof customerSegmentationFields>): boolean =>
  settings.activeWindowDays <= settings.dormantAfterDays;

const ACTIVE_BEYOND_DORMANT = {
  message: 'The active window cannot be longer than the dormant threshold',
  path: ['activeWindowDays'] as const,
};

export const customerSegmentationSettingsSchema = customerSegmentationFields.refine(
  activeFitsInsideDormant,
  { message: ACTIVE_BEYOND_DORMANT.message, path: [...ACTIVE_BEYOND_DORMANT.path] },
);
export type CustomerSegmentationSettings = z.infer<typeof customerSegmentationSettingsSchema>;

/** What a shop that has never opened the screen segments by. */
export const DEFAULT_CUSTOMER_SEGMENTATION: CustomerSegmentationSettings = Object.freeze({
  newWindowDays: 30,
  activeWindowDays: 90,
  dormantAfterDays: 180,
  highValuePaise: 50_000_00,
});

/**
 * Every threshold optional: the screen saves one at a time.
 *
 * The pair rule is checked here only when a patch carries both halves — a patch that moves one
 * of them against a value already stored is caught by the service, which re-validates the whole
 * resolved object rather than the fragment it was sent.
 */
export const updateCustomerSegmentationSettingsSchema = customerSegmentationFields
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' })
  .refine(
    (patch) =>
      patch.activeWindowDays === undefined ||
      patch.dormantAfterDays === undefined ||
      patch.activeWindowDays <= patch.dormantAfterDays,
    { message: ACTIVE_BEYOND_DORMANT.message, path: [...ACTIVE_BEYOND_DORMANT.path] },
  );
export type UpdateCustomerSegmentationSettingsInput = z.input<
  typeof updateCustomerSegmentationSettingsSchema
>;

/**
 * The thresholds resolved against a clock, once, into the four comparisons everything downstream
 * makes.
 *
 * This exists so the aggregation that counts the tiles and the JavaScript that labels a row in
 * the export are the SAME rule rather than two spellings of it: the pipeline compares stored
 * fields against these dates inside Mongo, `customerSegmentsOf` compares them in process, and
 * neither carries its own arithmetic.
 */
export interface CustomerSegmentBoundaries {
  /** `createdAt >= newSince` — registered inside the new window. */
  newSince: Date;
  /** `lastOrderAt >= activeSince` — ordered inside the active window. */
  activeSince: Date;
  /** `(lastOrderAt ?? createdAt) < dormantBefore` — no activity since. */
  dormantBefore: Date;
  /** `lifetimeValuePaise >= highValuePaise`. */
  highValuePaise: number;
}

const MS_PER_DAY = 86_400_000;

export function customerSegmentBoundaries(
  settings: CustomerSegmentationSettings,
  now: Date = new Date(),
): CustomerSegmentBoundaries {
  const at = (days: number): Date => new Date(now.getTime() - days * MS_PER_DAY);
  return {
    newSince: at(settings.newWindowDays),
    activeSince: at(settings.activeWindowDays),
    dormantBefore: at(settings.dormantAfterDays),
    highValuePaise: settings.highValuePaise,
  };
}

/** What the segmenting reads off a customer. Deliberately the smallest shape, so a lean row fits. */
export interface SegmentableCustomer {
  createdAt: Date | string;
  lastOrderAt: Date | string | null | undefined;
  lifetimeValuePaise: number;
}

/**
 * Every bucket this customer falls in, from the same boundaries the tiles counted with.
 *
 * "Last activity" is `lastOrderAt` when they have ordered and `createdAt` when they never have —
 * so somebody who registered a year ago and never came back is counted as dormant rather than
 * falling through every bucket, which is exactly the customer an owner wanted to find.
 */
export function customerSegmentsOf(
  customer: SegmentableCustomer,
  boundaries: CustomerSegmentBoundaries,
): CustomerSegment[] {
  const createdAt = new Date(customer.createdAt);
  const lastOrderAt = customer.lastOrderAt ? new Date(customer.lastOrderAt) : null;
  const lastActivityAt = lastOrderAt ?? createdAt;

  const segments: CustomerSegment[] = [];
  if (createdAt >= boundaries.newSince) segments.push('new');
  if (lastOrderAt && lastOrderAt >= boundaries.activeSince) segments.push('active');
  if (lastActivityAt < boundaries.dormantBefore) segments.push('dormant');
  if (customer.lifetimeValuePaise >= boundaries.highValuePaise) segments.push('high_value');
  return segments;
}

/**
 * The headline figures for the whole book. Counts are shop-wide — they are not narrowed by
 * whatever is typed in the search box, because they answer a question about the shop rather than
 * about the current result list.
 */
export const customerSegmentationSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  new: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  dormant: z.number().int().nonnegative(),
  highValue: z.number().int().nonnegative(),
  /** The thresholds these counts were computed against, so the tiles can say what they mean. */
  settings: customerSegmentationSettingsSchema,
});
export type CustomerSegmentationSummary = z.infer<typeof customerSegmentationSummarySchema>;
